import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useToast } from '../hooks/use-toast';
import FollowModal from '../components/FollowModal';
import { getAvatarUrl, getInitials } from '../utils/avatarUtils';
import {
  Radio,
  ExternalLink,
  Upload,
  Heart,
  Clock,
  Youtube,
  Star
} from 'lucide-react';

// Platform icons for live streaming
const PlatformIcon = ({ platform, className = "h-4 w-4" }) => {
  switch (platform) {
    case 'youtube':
      return <Youtube className={className} />;
    case 'tiktok':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
        </svg>
      );
    case 'twitch':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
        </svg>
      );
    case 'instagram':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      );
    case 'kick':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M1.333 0v24h5.334v-8l2.666 2.667L12 24h6.667l-5.334-8L18.667 8H12l-2.667 5.333L6.667 8V0z"/>
        </svg>
      );
    default:
      return <Radio className={className} />;
  }
};

const API_URL = '';  // Use relative URLs with proxy

// Helper function to format Cash App URL
const formatCashAppUrl = (cashAppValue) => {
  if (!cashAppValue) return null;
  
  // If it&apos;s already a full URL, return as-is
  if (cashAppValue.startsWith('https://cash.app/')) {
    return cashAppValue;
  }
  
  // Remove $ if present and create URL
  const username = cashAppValue.replace('$', '');
  return `https://cash.app/$${username}`;
};

const PublicReviewerProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [reviewer, setReviewer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [following, setFollowing] = useState(false);
  const [showFollowModal, setShowFollowModal] = useState(false);
  const isMountedRef = useRef(true);
  
  // P1: Check if user arrived here after submitting while reviewer was offline
  const isQueued = searchParams.get('queued') === 'true';
  const queuedSubmissionId = searchParams.get('submission');

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Initial fetch
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        console.log('Fetching reviewer profile for:', username);
        const response = await axios.get(`${API_URL}/api/reviewers/profile/${username}`);
        console.log('Reviewer data:', response.data);
        
        const reviewerData = response.data;
        
        // Get final avatar URL using SINGLE CANONICAL field
        const finalAvatarUrl = getAvatarUrl(reviewerData);
        reviewerData.finalAvatarUrl = finalAvatarUrl;
        console.log('Final avatar URL:', finalAvatarUrl);
        
        if (isMountedRef.current) {
          setReviewer(reviewerData);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching reviewer:', error);
        console.error('Error details:', error.response?.data);
        if (isMountedRef.current) {
          setError(error.response?.data?.detail || 'Failed to load reviewer profile');
          setLoading(false);
        }
      }
    };
    
    fetchProfile();
  }, [username]);

  // Show queued notification
  useEffect(() => {
    if (isQueued && queuedSubmissionId) {
      toast({
        title: "✅ Track successfully queued!",
        description: "You will be notified when the reviewer goes live.",
        duration: 8000
      });
    }
  }, [isQueued, queuedSubmissionId, toast]);

  const handleSubmitTrack = () => {
    // Check if reviewer is live (has active session with submissions enabled)
    if (!reviewer.is_live) {
      toast({
        title: "Reviewer is not live",
        description: "This reviewer is not currently accepting submissions. Follow them to get notified when they start reviewing!",
        duration: 5000
      });
      return;
    }
    
    // Navigate to upload page with reviewer ID
    navigate(`/upload?reviewer=${reviewer.id}`);
  };

  const handleFollowReviewer = () => {
    // Open the follow modal (no login required)
    setShowFollowModal(true);
  };

  // eslint-disable-next-line no-unused-vars
  const handleFollowSuccess = (_data) => {
    setFollowing(true);
    toast({
      title: "Success!",
      description: `You&apos;re now following ${reviewer.name}. You'll be notified when they start reviewing!`,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading profile...</div>
      </div>
    );
  }

  if (!reviewer && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">Reviewer Not Found</h1>
          <p className="text-gray-600 mb-4">{error || 'This reviewer profile doesn\'t exist.'}</p>
          <Button onClick={() => navigate('/reviewers')}>
            Browse All Reviewers
          </Button>
        </div>
      </div>
    );
  }

  // MONETIZATION GATING: Only include Cash App for paid plan reviewers
  const socialLinks = [
    ...(reviewer.canShowCashApp && reviewer.cash_app ? [{
      platform: 'Cash App', 
      url: formatCashAppUrl(reviewer.cash_app), 
      icon: '💵', 
      color: 'bg-green-500' 
    }] : []),
    { platform: 'Twitch', url: reviewer.twitch, icon: '🎮', color: 'bg-purple-500' },
    { platform: 'Instagram', url: reviewer.instagram, icon: '📸', color: 'bg-pink-500' },
    { platform: 'TikTok', url: reviewer.tiktok, icon: '🎵', color: 'bg-black' },
    { platform: 'Spotify', url: reviewer.spotify_link, icon: '🎧', color: 'bg-green-600' },
    { platform: 'SoundCloud', url: reviewer.soundcloud, icon: '🔊', color: 'bg-orange-500' },
    { platform: 'YouTube', url: reviewer.youtube, icon: '🎬', color: 'bg-red-600' },
  ].filter(link => link.url);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* P1: Queued submission banner */}
        {isQueued && (
          <div className="mb-6 bg-[#F2D857]/20 border-2 border-[#F2D857] rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-[#F2D857] rounded-full p-2">
                <Clock className="h-5 w-5 text-black" />
              </div>
              <div>
                <h3 className="font-semibold text-black">Your track is queued!</h3>
                <p className="text-sm text-gray-700">
                  {reviewer?.name || 'This reviewer'} is currently offline. Your submission will appear in their next live session.
                  {!following && ' Follow them to get notified when they start reviewing!'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Profile Header */}
        <Card className="p-8 mb-6 border-2 border-gray-200 relative">
          {/* MOBILE: Corner badges */}
          <div className="md:hidden">
            {/* Verified - Upper Left */}
            {reviewer.isProfileComplete && (
              <div className="absolute top-3 left-3">
                <Badge className="bg-emerald-500 text-white hover:bg-emerald-500 text-xs" data-testid="mobile-verified-badge">
                  <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Verified
                </Badge>
              </div>
            )}
            {/* PRO - Upper Right */}
            {reviewer.subscriptionActive && (
              <div className="absolute top-3 right-3">
                <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black hover:from-yellow-400 hover:to-amber-400 text-xs" data-testid="mobile-pro-badge">
                  <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  PRO
                </Badge>
              </div>
            )}
          </div>

          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <Avatar className="h-32 w-32 border-4 border-[#F2D857] overflow-hidden mt-6 md:mt-0">
              <AvatarImage 
                src={reviewer.finalAvatarUrl} 
                alt={reviewer.name}
                className="object-cover object-center w-full h-full"
              />
              <AvatarFallback className="bg-[#F2D857] text-black font-bold text-4xl">
                {getInitials(reviewer.name)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 text-center md:text-left">
              {/* DESKTOP: Inline badges */}
              <div className="hidden md:flex items-center justify-start gap-2 mb-2 flex-wrap">
                <h1 className="text-3xl font-bold font-montserrat">{reviewer.name}</h1>
                {/* Verified Badge */}
                {reviewer.isProfileComplete && (
                  <Badge className="bg-emerald-500 text-white hover:bg-emerald-500" data-testid="profile-verified-badge">
                    <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Verified
                  </Badge>
                )}
                {/* PRO Badge */}
                {reviewer.subscriptionActive && (
                  <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black hover:from-yellow-400 hover:to-amber-400" data-testid="profile-pro-badge">
                    <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    PRO
                  </Badge>
                )}
                {/* LIVE Badge + Platform Icons */}
                {reviewer.is_live && (
                  <>
                    <Badge className="bg-red-500 text-white animate-pulse" data-testid="profile-live-badge">
                      <Radio className="h-3 w-3 mr-1" />
                      LIVE
                    </Badge>
                    {/* Live streaming platform icons - colored */}
                    {reviewer.live_streaming_platforms && reviewer.live_streaming_platforms.length > 0 && (
                      <div className="flex items-center gap-1.5 ml-1">
                        {reviewer.live_streaming_platforms.includes('youtube') && (
                          <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center animate-pulse" title="Live on YouTube">
                            <Youtube className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                        {reviewer.live_streaming_platforms.includes('tiktok') && (
                          <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center animate-pulse" title="Live on TikTok">
                            <PlatformIcon platform="tiktok" className="w-3 h-3 text-white" />
                          </div>
                        )}
                        {reviewer.live_streaming_platforms.includes('twitch') && (
                          <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center animate-pulse" title="Live on Twitch">
                            <PlatformIcon platform="twitch" className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                        {reviewer.live_streaming_platforms.includes('instagram') && (
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center animate-pulse" title="Live on Instagram">
                            <PlatformIcon platform="instagram" className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                        {reviewer.live_streaming_platforms.includes('kick') && (
                          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center animate-pulse" title="Live on Kick">
                            <PlatformIcon platform="kick" className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* MOBILE: Name + LIVE with platforms */}
              <div className="md:hidden">
                <h1 className="text-3xl font-bold font-montserrat mb-2">{reviewer.name}</h1>
                {/* LIVE Badge + Platform Icons Row */}
                {reviewer.is_live && (
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Badge className="bg-red-500 text-white animate-pulse" data-testid="mobile-live-badge">
                      <Radio className="h-3 w-3 mr-1" />
                      LIVE
                    </Badge>
                    {/* Live streaming platform icons - colored */}
                    {reviewer.live_streaming_platforms && reviewer.live_streaming_platforms.length > 0 && (
                      <div className="flex items-center gap-1.5">
                        {reviewer.live_streaming_platforms.includes('youtube') && (
                          <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center animate-pulse" title="Live on YouTube">
                            <Youtube className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                        {reviewer.live_streaming_platforms.includes('tiktok') && (
                          <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center animate-pulse" title="Live on TikTok">
                            <PlatformIcon platform="tiktok" className="w-3 h-3 text-white" />
                          </div>
                        )}
                        {reviewer.live_streaming_platforms.includes('twitch') && (
                          <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center animate-pulse" title="Live on Twitch">
                            <PlatformIcon platform="twitch" className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                        {reviewer.live_streaming_platforms.includes('instagram') && (
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center animate-pulse" title="Live on Instagram">
                            <PlatformIcon platform="instagram" className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                        {reviewer.live_streaming_platforms.includes('kick') && (
                          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center animate-pulse" title="Live on Kick">
                            <PlatformIcon platform="kick" className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <p className="text-gray-600 mb-1">@{reviewer.username}</p>
              {reviewer.bio && (
                <p className="text-gray-700 mt-3 mb-4">{reviewer.bio}</p>
              )}
              
              {reviewer.genres && reviewer.genres.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
                  {reviewer.genres.map((genre, index) => (
                    <Badge key={index} variant="outline" className="border-[#F2D857] text-[#F2D857]">
                      {genre}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Stats */}
              <div className="flex gap-6 justify-center md:justify-start text-sm">
                <div className="text-center md:text-left">
                  <div className="font-bold text-xl">{reviewer.totalReviews || 0}</div>
                  <div className="text-gray-600">Reviews</div>
                </div>
                <div className="text-center md:text-left">
                  <div className="font-bold text-xl flex items-center justify-center md:justify-start">
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500 mr-1" />
                    {reviewer.average_rating?.toFixed(1) || 'N/A'}
                  </div>
                  <div className="text-gray-600">Rating</div>
                </div>
                <div className="text-center md:text-left">
                  <div className="font-bold text-xl">${reviewer.total_earnings?.toFixed(0) || 0}</div>
                  <div className="text-gray-600">Earned</div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Social Links */}
        {socialLinks.length > 0 && (
          <Card className="p-6 mb-6 border-2 border-gray-200">
            <h3 className="text-xl font-bold font-montserrat mb-4">Connect</h3>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white hover:opacity-90 transition ${link.color}`}
                >
                  <span>{link.icon}</span>
                  <span className="font-semibold">{link.platform}</span>
                  <ExternalLink className="h-4 w-4" />
                </a>
              ))}
            </div>
          </Card>
        )}

        {/* CTA */}
        <Card className="p-4 md:p-8 border-2 border-[#F2D857] bg-gradient-to-r from-[#F2D857]/10 to-transparent review-cta-section">
          <div className="text-center flex flex-col items-center gap-4">
            <h3 className="font-bold font-montserrat mb-2" style={{ fontSize: 'clamp(18px, 4vw, 28px)' }}>
              🎧 Ready to get your music reviewed?
            </h3>
            <p className="text-gray-600 mb-2 max-w-2xl" style={{ fontSize: 'clamp(14px, 3.5vw, 16px)' }}>
              Submit your track to {reviewer.name} and get professional feedback
            </p>
            
            {/* Desktop Layout */}
            <div className="hidden md:flex gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-bold shadow-md"
                onClick={handleSubmitTrack}
              >
                <Upload className="h-5 w-5 mr-2" />
                Submit Your Track
              </Button>
              {!reviewer.is_live && (
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-[#F2D857] hover:bg-[#F2D857]/10"
                  onClick={handleFollowReviewer}
                >
                  <Heart className={`h-5 w-5 mr-2 ${following ? 'fill-current' : ''}`} />
                  Follow for Notifications
                </Button>
              )}
              {reviewer.is_live && reviewer.sessionId && (
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-black hover:bg-black/5"
                  onClick={() => navigate(`/session/${reviewer.sessionId}`)}
                >
                  <Radio className="h-5 w-5 mr-2" />
                  Join Live Session
                </Button>
              )}
            </div>

            {/* Mobile Layout */}
            <div className="md:hidden flex flex-col items-center gap-3 w-full">
              <Button 
                className="bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-bold shadow-md w-[90%] py-6 text-base"
                onClick={handleSubmitTrack}
              >
                <Upload className="h-5 w-5 mr-2" />
                Submit Your Track
              </Button>
              {!reviewer.is_live && (
                <Button 
                  variant="outline"
                  className="border-2 border-[#F2D857] hover:bg-[#F2D857]/10 w-[90%] py-6 text-base"
                  onClick={handleFollowReviewer}
                >
                  <Heart className={`h-5 w-5 mr-2 ${following ? 'fill-current' : ''}`} />
                  Follow for Notifications
                </Button>
              )}
              {reviewer.is_live && reviewer.sessionId && (
                <Button 
                  variant="outline"
                  className="border-2 border-black hover:bg-black/5 w-[90%] py-6 text-base"
                  onClick={() => navigate(`/session/${reviewer.sessionId}`)}
                >
                  <Radio className="h-5 w-5 mr-2" />
                  Join Live Session
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Follow Modal */}
      <FollowModal
        reviewer={reviewer}
        isOpen={showFollowModal}
        onClose={() => setShowFollowModal(false)}
        onSuccess={handleFollowSuccess}
      />
    </div>
  );
};

export default PublicReviewerProfile;
